import type { TFunction } from 'i18next';
import type { LucideIcon } from 'lucide-react';
import { CalendarDays, Languages, MapPin, School, Tags, UserRound } from 'lucide-react';
import type { ResponsiveTableColumnSize } from '@shared/ui';
import type {
  CrmCourseProfileBaseResponse,
  CrmCourseProfileRegistrationsResponse,
  CrmCourseProfileSort,
  CrmCourseProfileTab,
} from '../api/schemas';

export type Course = CrmCourseProfileBaseResponse['data'];
export type Registration = CrmCourseProfileRegistrationsResponse['data']['items'][number];
export type Label = { key: string; fallback: string };
export type Field = {
  id: string;
  label: Label;
  value: string | number | boolean | null | undefined | readonly (string | null)[];
  kind?: 'date' | 'list';
};
export type Section = { id: string; title: Label; fields: Field[] };
const label = (key: string, fallback: string): Label => ({
  key: `crm.courseProfile.${key}`,
  fallback,
});
const field = (
  id: string,
  fallback: string,
  value: Field['value'],
  kind?: Field['kind']
): Field => ({ id, label: label(`fields.${id}`, fallback), value, kind });
export const courseTabs: { id: CrmCourseProfileTab; label: Label }[] = [
  ['general', 'General'],
  ['team', 'Course Team'],
  ['registrations', 'Registrations'],
  ['students', 'Students and mentees'],
  ['materials', 'Materials'],
  ['completion-form', 'Completion Form'],
  ['marketplace', 'Marketplace'],
].map(([id, fallback]) => ({
  id: id as CrmCourseProfileTab,
  label: label(`tabs.${id}`, fallback),
}));
export const courseTeamGroups = [
  ['leadInstructor', 'Lead Instructor'],
  ['coInstructor', 'Co-instructors'],
  ['assistant', 'Assistants'],
  ['invitedGuest', 'Invited guests'],
  ['school', 'School'],
] as const;
export type CourseSortField =
  | 'registrationSubmittedAt'
  | 'registrationNumber'
  | 'registrationCertifiedAt'
  | 'studentName';
export const registrationSortFields: Readonly<Record<string, CourseSortField | undefined>> = {
  registration: 'registrationNumber',
  enrolled: 'registrationSubmittedAt',
  certifiedAt: 'registrationCertifiedAt',
};
export function courseSortState(sort: CrmCourseProfileSort | undefined, people = false) {
  const [field, direction] = (
    sort ?? (people ? 'studentName.asc' : 'registrationSubmittedAt.desc')
  ).split('.');
  return { field: field as CourseSortField, direction: direction as 'asc' | 'desc' };
}

export function courseHeaderFields(course: Course): (Field & { icon: LucideIcon })[] {
  return [
    { ...field('start', 'Start', course.startAt, 'date'), icon: CalendarDays },
    ...(course.endAt ? [{ ...field('end', 'End', course.endAt, 'date'), icon: CalendarDays }] : []),
    {
      ...field('leadInstructor', 'Lead instructor', course.team.leadInstructor?.name),
      icon: UserRound,
    },
    { ...field('location', 'Location', course.location), icon: MapPin },
    { ...field('school', 'School', course.school?.name), icon: School },
    { ...field('language', 'Language', course.language), icon: Languages },
    { ...field('format', 'Format', course.format), icon: Tags },
  ].filter((field) => {
    const value = field.value;
    return value !== null && value !== undefined && String(value).trim() !== '';
  });
}
export function courseHeading(course: Course): string | null {
  const name = course.title?.trim() || course.courseType?.name?.trim();
  return [name, course.number ? `№${course.number}` : null].filter(Boolean).join(' ') || null;
}

function courseOccupancy(course: Course): string | number | null {
  const { spotsTaken, total, percentTaken } = course.capacity;
  const occupied = total === null ? spotsTaken : `${String(spotsTaken)}/${String(total)}`;
  return percentTaken === null ? occupied : `${String(occupied)} (${String(percentTaken)}%)`;
}
export function courseGeneralSections(course: Course): Section[] {
  const a = course.administration;
  const canViewAdministration = 'superAdminNote' in a;
  return [
    {
      id: 'details',
      title: label('general.details', 'Course details'),
      fields: [
        field('number', 'Course number', course.number),
        field('length', 'Length', course.length),
        field('description', 'Description', course.description),
        field('country', 'Country', course.country),
        field('location', 'Location', course.location),
        field('weather', 'Weather', course.weather),
        field('waterTemperature', 'Water temperature', course.waterTemperature),
        field('poolAccess', 'Pool access', course.poolAccess),
        field('logistics', 'Logistics', a.logistics),
        field('organization', 'Organization guidance', a.organizationGuidance),
        field('seaConditions', 'Sea conditions', a.seaConditions),
        field('issueGuidance', 'Issue guidance', a.issueGuidance),
      ],
    },
    {
      id: 'teamSchool',
      title: label('general.teamSchool', 'Team and school'),
      fields: [
        field('leadInstructor', 'Lead instructor', course.team.leadInstructor?.name),
        field('school', 'School', course.school?.name),
      ],
    },
    {
      id: 'enrollment',
      title: label('general.enrollment', 'Enrollment'),
      fields: [
        field('spotsTaken', 'Spots taken', courseOccupancy(course)),
        field('paymentPermission', 'Payment permission', course.paymentPermission),
        field('registrationPermission', 'Registration permission', course.registrationPermission),
      ],
    },
    ...(canViewAdministration
      ? [
          {
            id: 'administration',
            title: label('general.administration', 'Administration'),
            fields: [
              field('sku', 'SKU ID', a.sku),
              field('createdAt', 'Created at', a.createdAt, 'date'),
              field('creator', 'Created by', a.creator?.name),
              field('submittedAt', 'Submitted at', a.submittedAt, 'date'),
              field('updatedAt', 'Updated at', a.updatedAt, 'date'),
              field('freeAccessRoles', 'Free-access roles', course.freeAccess.roles, 'list'),
              field(
                'freeAccessUsers',
                'Free-access users',
                course.freeAccess.users.map((user) => user.name),
                'list'
              ),
              field('superAdminNote', 'Super admin note', a.superAdminNote),
            ],
          },
        ]
      : []),
  ];
}
export function formatCourseDate(value: string | null | undefined, locale: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}
export const registrationColumns: { id: string; label: Label; size: ResponsiveTableColumnSize }[] =
  [
    { id: 'freediver', label: label('registration.freediver', 'Freediver'), size: 'identity' },
    {
      id: 'registration',
      label: label('registration.number', 'Registration number'),
      size: 'compact',
    },
    { id: 'payment', label: label('registration.payment', 'Instructor payment'), size: 'wide' },
    { id: 'enrolled', label: label('registration.enrolled', 'Enrolled date'), size: 'date' },
    { id: 'modules', label: label('registration.modules', 'Online Module'), size: 'status' },
    { id: 'exam', label: label('registration.exam', 'Exam'), size: 'status' },
    {
      id: 'completion',
      label: label('registration.completion', 'Completion Form'),
      size: 'status',
    },
    { id: 'certificate', label: label('registration.certificate', 'Certificate'), size: 'status' },
    { id: 'certifiedAt', label: label('registration.certifiedAt', 'Certified date'), size: 'date' },
  ];

export function isFieldList(value: Field['value']): value is readonly (string | null)[] {
  return Array.isArray(value);
}
export function fieldText(field: Field, locale: string, t: TFunction): string {
  const missing = t('crm.missingValue', 'Not provided');
  if (field.value == null || (typeof field.value === 'string' && !field.value.trim()))
    return missing;
  if (field.kind === 'date') return formatCourseDate(String(field.value), locale) ?? missing;
  if (typeof field.value === 'boolean')
    return field.value
      ? t('crm.courseProfile.values.yes', 'Yes')
      : t('crm.courseProfile.values.no', 'No');
  if (isFieldList(field.value))
    return field.value.length
      ? field.value.map((value) => value ?? missing).join(', ')
      : t('crm.courseProfile.states.none', 'None');
  return String(field.value);
}
